/**
 * Failure handling for AI message composition.
 *
 * Composition failures are silent today: the lazy query has no `onError` and
 * the hook reads through an optional chain, so a failed call is
 * indistinguishable from a model that returned nothing. These types describe
 * the two failures worth telling the agent apart:
 *
 * - `Retryable` — the model call errored, timed out, or the network dropped.
 *   The common case. Retrying costs nothing because neither the agent's typed
 *   content nor the macro's attached actions are touched on failure.
 * - `PromptUnavailable` — the configured prompt is inactive or not available to
 *   this user. Rarer, and retrying cannot help, so it must read differently.
 */
export enum AiGenerationErrorKind {
  Retryable = 'retryable',
  PromptUnavailable = 'promptUnavailable',
}

/**
 * Marker the composition resolver puts on the unavailable-prompt failure.
 * Matched against the GraphQL error message, which is the only channel that
 * survives the resolver → Apollo hop without new plumbing.
 */
export const AI_PROMPT_UNAVAILABLE_CODE = 'AI_PROMPT_UNAVAILABLE';

export interface AiGenerationError {
  kind: AiGenerationErrorKind;
  message: string;
}

/**
 * Everything needed to replay a generation, captured when it is dispatched so
 * that `retryGeneration` does not depend on live editor or picker state.
 */
export interface AiGenerationRequest {
  userId: string;
  promptId?: string;
  promptName?: string;
  customPrompt?: string;
  userInput?: string;
  correction?: string;
  previousResponseId?: string;
  conversationMessageId?: string;
}

const RETRYABLE_MESSAGE =
  "Couldn't generate a draft. Something went wrong on our side. Your message is untouched — try again.";

const PROMPT_UNAVAILABLE_MESSAGE =
  "This AI prompt is no longer available to you. Ask an admin to check it — retrying won't help.";

/**
 * Maps a composition failure onto the kinds above. Anything unrecognised is
 * treated as retryable: offering a retry that fails again is a smaller cost
 * than telling an agent to give up on a failure that would have cleared.
 */
export function classifyAiGenerationError(error: unknown): AiGenerationError {
  if (mentionsUnavailablePrompt(error)) {
    return {
      kind: AiGenerationErrorKind.PromptUnavailable,
      message: PROMPT_UNAVAILABLE_MESSAGE,
    };
  }

  return { kind: AiGenerationErrorKind.Retryable, message: RETRYABLE_MESSAGE };
}

function mentionsUnavailablePrompt(error: unknown): boolean {
  return collectMessages(error).some((message) =>
    message.includes(AI_PROMPT_UNAVAILABLE_CODE),
  );
}

function collectMessages(error: unknown): string[] {
  if (!error || typeof error !== 'object') {
    return [];
  }

  const { message, graphQLErrors } = error as {
    message?: unknown;
    graphQLErrors?: { message?: unknown }[];
  };

  return [
    typeof message === 'string' ? message : '',
    ...(Array.isArray(graphQLErrors)
      ? graphQLErrors.map((graphQLError) =>
          typeof graphQLError?.message === 'string' ? graphQLError.message : '',
        )
      : []),
  ].filter(Boolean);
}
